import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useI18n } from "../../../hooks/useI18n";
import { theme } from "../../../layout/Theme";
import { type EvcVaccinationMatch } from "./evcVaccinationReconciliation";
import { VaccinationToImport } from "./types";

export function VaccinationSelectionAccordion({
  vaccinations,
  selectedCount,
  targetMode,
  selectedVaccinationIds,
  reconciledVaccinationById,
  onToggleVaccination,
}: {
  vaccinations: VaccinationToImport[];
  selectedCount: number;
  targetMode: "new" | "existing";
  selectedVaccinationIds: Set<number>;
  reconciledVaccinationById: Map<number, EvcVaccinationMatch>;
  onToggleVaccination: (vaccinationId: number) => void;
}) {
  const { t } = useI18n();

  if (vaccinations.length === 0) {
    return null;
  }

  return (
    <Accordion
      variant="outlined"
      disableGutters
      sx={{
        border: `1px solid ${theme.palette.neutral[200]}`,
        borderRadius: 2,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: "row-reverse", gap: 1 }}>
        <Typography variant="subtitle2" color={theme.palette.neutral[700]}>
          {t("evc_onboarding.vaccinations_summary", { selected: selectedCount, total: vaccinations.length })}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <List disablePadding>
          {vaccinations.map((vaccination, index) => {
            const match = reconciledVaccinationById.get(vaccination.id);
            const isExactMatch = targetMode === "existing" && match?.status === "exact";
            const isSelected = selectedVaccinationIds.has(vaccination.id);

            return (
              <ListItem
                key={vaccination.id}
                divider={index < vaccinations.length - 1}
                onClick={() => onToggleVaccination(vaccination.id)}
                sx={{ cursor: isExactMatch ? "default" : "pointer" }}
              >
                <Checkbox checked={isSelected} disabled={isExactMatch} sx={{ mr: 1 }} />
                <ListItemText
                  primary={vaccination.vaccineName}
                  secondary={`${vaccination.performedOn} • ${vaccination.diseasesLabel}`}
                  slotProps={{
                    primary: { variant: "body2", color: theme.palette.neutral[700] },
                    secondary: { variant: "caption", color: theme.palette.neutral[500] },
                  }}
                />
                {targetMode === "existing" && match && (
                  <Chip
                    label={t(`evc_onboarding.status_${match.status}`)}
                    color={match.status === "exact" ? "success" : "default"}
                    variant={match.status === "exact" ? "filled" : "outlined"}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  );
}
